const EARTH_RADIUS_MILES = 3958.8;

if (!customElements.get('pickup-availability')) {
  customElements.define(
    'pickup-availability',
    class PickupAvailability extends HTMLElement {
      constructor() {
        super();

        if (!this.hasAttribute('available')) return;

        this.errorHtml = this.querySelector('template').content.firstElementChild.cloneNode(true);
        this.onClickRefreshList = this.onClickRefreshList.bind(this);
        this.onZipSubmit = this.onZipSubmit.bind(this);
        this.onZipClear = this.onZipClear.bind(this);
        this.onZipInput = this.onZipInput.bind(this);
        this.lookupAbortController = null;
        this.ipLookupAbortController = null;
        this.fetchAvailability(this.dataset.variantId);
      }

      disconnectedCallback() {
        this.removeZipListeners();

        if (this.lookupAbortController) {
          this.lookupAbortController.abort();
        }

        if (this.ipLookupAbortController) {
          this.ipLookupAbortController.abort();
        }
      }

      fetchAvailability(variantId) {
        if (!variantId) return;

        let rootUrl = this.dataset.rootUrl;
        if (!rootUrl.endsWith('/')) {
          rootUrl = rootUrl + '/';
        }
        const variantSectionUrl = `${rootUrl}variants/${variantId}/?section_id=pickup-availability`;

        fetch(variantSectionUrl)
          .then((response) => response.text())
          .then((text) => {
            const sectionInnerHTML = new DOMParser()
              .parseFromString(text, 'text/html')
              .querySelector('.shopify-section');
            this.renderPreview(sectionInnerHTML);
          })
          .catch((e) => {
            const button = this.querySelector('button');
            if (button) button.removeEventListener('click', this.onClickRefreshList);
            this.renderError();
          });
      }

      onClickRefreshList() {
        this.fetchAvailability(this.dataset.variantId);
      }

      update(variant) {
        if (variant?.available) {
          this.fetchAvailability(variant.id);
        } else {
          this.removeAttribute('available');
          this.innerHTML = '';
        }
      }

      renderError() {
        this.innerHTML = '';
        this.appendChild(this.errorHtml);

        this.querySelector('button').addEventListener('click', this.onClickRefreshList);
      }

      renderPreview(sectionInnerHTML) {
        this.removeZipListeners();

        const drawer = document.querySelector('pickup-availability-drawer');
        if (drawer) drawer.remove();
        if (!sectionInnerHTML.querySelector('pickup-availability-preview')) {
          this.innerHTML = '';
          this.removeAttribute('available');
          return;
        }

        this.innerHTML = sectionInnerHTML.querySelector('pickup-availability-preview').outerHTML;
        this.setAttribute('available', '');

        this.drawerElement = sectionInnerHTML.querySelector('pickup-availability-drawer');
        document.body.appendChild(this.drawerElement);

        const colorClassesToApply = this.dataset.productPageColorScheme.split(' ');
        colorClassesToApply.forEach((colorClass) => {
          this.drawerElement.classList.add(colorClass);
        });

        const button = this.querySelector('#ShowPickupAvailabilityDrawer');
        if (button)
          button.addEventListener('click', (evt) => {
            this.drawerElement.show(evt.target);
          });

        this.initializeZipLookup();
      }

      initializeZipLookup() {
        this.zipForm = this.querySelector('[data-pickup-zip-form]');
        this.zipInput = this.querySelector('[data-pickup-zip-input]');
        this.zipSubmitButton = this.querySelector('[data-pickup-zip-submit]');
        this.zipClearButton = this.querySelector('[data-pickup-zip-clear]');
        this.zipStatus = this.querySelector('[data-pickup-zip-status]');
        this.previewState = this.querySelector('[data-pickup-preview-state]');
        this.previewLocationName = this.querySelector('[data-pickup-preview-location-name]');
        this.previewAddress = this.querySelector('[data-pickup-preview-address]');
        this.previewTime = this.querySelector('[data-pickup-preview-time]');

        if (!this.zipForm || !this.drawerElement) return;

        this.locationItems = Array.from(this.drawerElement.querySelectorAll('[data-pickup-location-item]'));
        if (!this.locationItems.length) return;

        if (this.locationItems.length < 2) {
          this.restoreDefaultLocationOrder();
          return;
        }

        this.zipForm.addEventListener('submit', this.onZipSubmit);
        if (this.zipInput) {
          this.zipInput.addEventListener('input', this.onZipInput);
        }
        if (this.zipClearButton) {
          this.zipClearButton.addEventListener('click', this.onZipClear);
        }

        const storedZip = this.getStoredZip();
        if (storedZip) {
          if (this.zipInput) this.zipInput.value = storedZip;
          this.toggleClearButtonVisibility(storedZip);
          this.applyClosestLocationByZip(storedZip, {
            silenceErrors: true,
            showLoadingStatus: false,
            showAppliedStatus: false,
            persistZip: true,
          });
          return;
        }

        this.toggleClearButtonVisibility(this.zipInput?.value || '');

        this.restoreDefaultLocationOrder();
        this.autoPopulateZipFromIp();
      }

      removeZipListeners() {
        if (this.zipForm) {
          this.zipForm.removeEventListener('submit', this.onZipSubmit);
        }

        if (this.zipInput) {
          this.zipInput.removeEventListener('input', this.onZipInput);
        }

        if (this.zipClearButton) {
          this.zipClearButton.removeEventListener('click', this.onZipClear);
        }
      }

      onZipInput(event) {
        this.toggleClearButtonVisibility(event?.target?.value || '');
      }

      onZipSubmit(event) {
        event.preventDefault();
        this.applyClosestLocationByZip(this.zipInput?.value || '');
      }

      onZipClear() {
        this.clearStoredZip();

        if (this.zipInput) {
          this.zipInput.value = '';
        }

        this.toggleClearButtonVisibility('');

        this.restoreDefaultLocationOrder();
        this.setZipStatus('');
      }

      async applyClosestLocationByZip(rawZip, options = {}) {
        const silenceErrors = Boolean(options.silenceErrors);
        const showLoadingStatus = options.showLoadingStatus !== false;
        const showAppliedStatus = options.showAppliedStatus !== false;
        const persistZip = options.persistZip !== false;
        const geocodeCountry = options.geocodeCountry;

        const zip = this.normalizeZip(rawZip, geocodeCountry);
        if (!zip) {
          if (silenceErrors) {
            this.clearStoredZip();
            this.restoreDefaultLocationOrder();
            this.toggleClearButtonVisibility(this.zipInput?.value || '');
            return;
          }

          if (!silenceErrors) {
            this.setZipStatus(this.getLabel('pickupInvalidZipLabel'));
          }
          return;
        }

        if (showLoadingStatus) {
          this.setZipStatus(this.getLabel('pickupLoadingZipLabel'));
        }

        if (this.zipSubmitButton) {
          this.zipSubmitButton.disabled = true;
        }

        try {
          const coordinates = await this.lookupCoordinatesByZip(zip, geocodeCountry);
          const closestLocation = this.findClosestLocation(coordinates.latitude, coordinates.longitude);

          if (!closestLocation) {
            throw new Error('No matching pickup locations found.');
          }

          this.applyPreferredLocation(closestLocation);
          if (persistZip) {
            this.storeZip(zip);
          }

          this.toggleClearButtonVisibility(this.zipInput?.value || zip);

          if (showAppliedStatus) {
            const appliedTemplate = this.getLabel('pickupZipAppliedLabel');
            this.setZipStatus(appliedTemplate.replace('__ZIP__', zip));
          }
        } catch (error) {
          if (error.name === 'AbortError') return;

          if (silenceErrors) {
            this.clearStoredZip();
            this.restoreDefaultLocationOrder();
            this.toggleClearButtonVisibility(this.zipInput?.value || '');
            return;
          }

          this.setZipStatus(this.getLabel('pickupZipErrorLabel'));
        } finally {
          if (this.zipSubmitButton) {
            this.zipSubmitButton.disabled = false;
          }
        }
      }

      async autoPopulateZipFromIp() {
        if (!this.zipInput) return;
        if (this.zipInput.value.trim()) return;

        if (this.ipLookupAbortController) {
          this.ipLookupAbortController.abort();
        }

        this.ipLookupAbortController = new AbortController();

        try {
          const ipLocation = await this.fetchIpLocationData();
          if (!ipLocation) return;

          let ipPostal = ipLocation.postal || '';
          let ipCountryCode = String(ipLocation.countryCode || '').toLowerCase();

          if (!ipPostal && Number.isFinite(ipLocation.latitude) && Number.isFinite(ipLocation.longitude)) {
            const reverseLocation = await this.reverseGeocodePostal(ipLocation.latitude, ipLocation.longitude);
            if (reverseLocation) {
              ipPostal = reverseLocation.postal || '';
              if (!ipCountryCode && reverseLocation.countryCode) {
                ipCountryCode = String(reverseLocation.countryCode).toLowerCase();
              }
            }
          }

          const normalizedZip = this.normalizeZip(ipPostal, ipCountryCode || undefined);
          if (!normalizedZip) return;

          if (this.zipInput.value.trim()) return;

          this.zipInput.value = normalizedZip;
          this.toggleClearButtonVisibility(normalizedZip);

          this.applyClosestLocationByZip(normalizedZip, {
            silenceErrors: true,
            showLoadingStatus: false,
            showAppliedStatus: false,
            persistZip: true,
            geocodeCountry: ipCountryCode || undefined,
          });
        } catch (error) {
          if (error.name === 'AbortError') return;
        }
      }

      async fetchIpLocationData() {
        const providers = [
          {
            url: 'https://ipwho.is/',
            parse: (payload) => {
              if (!payload || payload.success === false) return null;

              return {
                postal: payload.postal || payload.postal_code || '',
                countryCode: payload.country_code || payload.country_code2 || '',
                latitude: Number(payload.latitude),
                longitude: Number(payload.longitude),
              };
            },
          },
          {
            url: 'https://ipinfo.io/json',
            parse: (payload) => {
              if (!payload) return null;

              const locParts = String(payload.loc || '').split(',');
              const latitude = Number(locParts[0]);
              const longitude = Number(locParts[1]);

              return {
                postal: payload.postal || '',
                countryCode: payload.country || '',
                latitude,
                longitude,
              };
            },
          },
          {
            url: 'https://ipapi.co/json/',
            parse: (payload) => {
              if (!payload || payload.error) return null;

              return {
                postal: payload.postal || payload.postcode || '',
                countryCode: payload.country_code || '',
                latitude: Number(payload.latitude),
                longitude: Number(payload.longitude),
              };
            },
          },
        ];

        for (const provider of providers) {
          try {
            const response = await fetch(provider.url, {
              signal: this.ipLookupAbortController.signal,
            });

            if (!response.ok) {
              continue;
            }

            const payload = await response.json();
            const parsed = provider.parse(payload);
            if (parsed && (parsed.postal || '').trim()) {
              return parsed;
            }
          } catch (error) {
            if (error.name === 'AbortError') {
              throw error;
            }
          }
        }

        return null;
      }

      async reverseGeocodePostal(latitude, longitude) {
        const endpoint = `https://api.bigdatacloud.net/data/reverse-geocode-client?latitude=${encodeURIComponent(
          latitude
        )}&longitude=${encodeURIComponent(longitude)}&localityLanguage=en`;

        const response = await fetch(endpoint, {
          signal: this.ipLookupAbortController.signal,
        });

        if (!response.ok) {
          return null;
        }

        const payload = await response.json();
        const postal = payload?.postcode || '';
        const countryCode = payload?.countryCode || '';

        if (!postal) {
          return null;
        }

        return {
          postal,
          countryCode,
        };
      }

      async lookupCoordinatesByZip(zip, countryOverride) {
        if (this.lookupAbortController) {
          this.lookupAbortController.abort();
        }

        this.lookupAbortController = new AbortController();
        const country = String(countryOverride || this.getCountryCode() || 'us').toLowerCase();
        const endpoint = `https://api.zippopotam.us/${country}/${encodeURIComponent(zip)}`;
        const response = await fetch(endpoint, {
          signal: this.lookupAbortController.signal,
        });

        if (!response.ok) {
          throw new Error('Failed to geocode zip code.');
        }

        const payload = await response.json();
        const place = payload?.places?.[0];
        const latitude = Number(place?.latitude);
        const longitude = Number(place?.longitude);

        if (!Number.isFinite(latitude) || !Number.isFinite(longitude)) {
          throw new Error('Missing latitude or longitude for zip code.');
        }

        return { latitude, longitude };
      }

      normalizeZip(rawZip, countryOverride) {
        const country = String(countryOverride || this.getCountryCode() || 'us').toLowerCase();

        if (country === 'us') {
          const digitsOnly = String(rawZip || '').replace(/\D/g, '');
          if (digitsOnly.length === 5) return digitsOnly;
          if (digitsOnly.length === 9) return digitsOnly.slice(0, 5);
          return null;
        }

        const normalized = String(rawZip || '').trim().toUpperCase();
        return normalized.length >= 3 ? normalized : null;
      }

      getCountryCode() {
        return (this.dataset.pickupZipCountry || 'us').toLowerCase();
      }

      findClosestLocation(latitude, longitude) {
        const candidates = this.locationItems
          .map((item) => {
            const itemLatitude = Number(item.dataset.locationLatitude);
            const itemLongitude = Number(item.dataset.locationLongitude);

            return {
              item,
              available: item.dataset.locationAvailable === 'true',
              index: Number(item.dataset.locationIndex),
              latitude: itemLatitude,
              longitude: itemLongitude,
              hasCoordinates: Number.isFinite(itemLatitude) && Number.isFinite(itemLongitude),
            };
          })
          .filter((candidate) => candidate.hasCoordinates);

        const availableCandidates = candidates.filter((candidate) => candidate.available);
        const rankedCandidates = (availableCandidates.length ? availableCandidates : candidates)
          .map((candidate) => ({
            ...candidate,
            distance: this.calculateDistanceMiles(latitude, longitude, candidate.latitude, candidate.longitude),
          }))
          .sort((first, second) => {
            if (first.distance !== second.distance) return first.distance - second.distance;
            return first.index - second.index;
          });

        if (rankedCandidates.length > 0) {
          return rankedCandidates[0].item;
        }

        return [...this.locationItems].sort(
          (first, second) => Number(first.dataset.locationIndex) - Number(second.dataset.locationIndex)
        )[0];
      }

      calculateDistanceMiles(lat1, lon1, lat2, lon2) {
        const degreesToRadians = (degrees) => (degrees * Math.PI) / 180;

        const dLat = degreesToRadians(lat2 - lat1);
        const dLon = degreesToRadians(lon2 - lon1);
        const a =
          Math.sin(dLat / 2) * Math.sin(dLat / 2) +
          Math.cos(degreesToRadians(lat1)) *
            Math.cos(degreesToRadians(lat2)) *
            Math.sin(dLon / 2) *
            Math.sin(dLon / 2);
        const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
        return EARTH_RADIUS_MILES * c;
      }

      applyPreferredLocation(locationItem) {
        const locationsList = this.drawerElement.querySelector('[data-store-availability-drawer-content]');
        if (locationsList && locationItem.parentElement === locationsList) {
          locationsList.prepend(locationItem);
        }

        this.setClosestBadge(locationItem);
        this.syncPreviewWithLocation(locationItem);
      }

      restoreDefaultLocationOrder() {
        if (!this.drawerElement || !this.locationItems?.length) return;

        const locationsList = this.drawerElement.querySelector('[data-store-availability-drawer-content]');
        if (!locationsList) return;

        const sortedLocations = [...this.locationItems].sort(
          (first, second) => Number(first.dataset.locationIndex) - Number(second.dataset.locationIndex)
        );

        sortedLocations.forEach((locationItem) => {
          locationsList.appendChild(locationItem);
        });

        this.setClosestBadge(null);
        this.syncPreviewWithLocation(sortedLocations[0]);
      }

      syncPreviewWithLocation(locationItem) {
        if (!locationItem) return;

        const isAvailable = locationItem.dataset.locationAvailable === 'true';

        if (this.previewState) {
          this.previewState.textContent = isAvailable
            ? this.getLabel('pickupAvailableLabel')
            : this.getLabel('pickupUnavailableLabel');
        }

        if (this.previewLocationName) {
          this.previewLocationName.textContent = locationItem.dataset.locationName || '';
        }

        if (this.previewAddress) {
          const sourceAddress = locationItem.querySelector('.pickup-availability-address');
          this.previewAddress.innerHTML = sourceAddress ? sourceAddress.innerHTML : '';
          this.previewAddress.hidden = !isAvailable;
        }

        if (this.previewTime) {
          const pickupTime = locationItem.dataset.locationPickupTime || '';
          const timeText = this.previewTime.querySelector('span');
          if (timeText) {
            timeText.textContent = isAvailable ? pickupTime : '';
          } else {
            this.previewTime.textContent = isAvailable ? pickupTime : '';
          }
          this.previewTime.hidden = !isAvailable || pickupTime.length === 0;
        }
      }

      setClosestBadge(locationItem) {
        this.locationItems.forEach((item) => {
          const badge = item.querySelector('[data-pickup-closest-badge]');
          if (!badge) return;

          badge.hidden = item !== locationItem;
        });
      }

      setZipStatus(message) {
        if (!this.zipStatus) return;
        this.zipStatus.textContent = message || '';
      }

      toggleClearButtonVisibility(value) {
        if (!this.zipClearButton) return;
        const shouldHide = !(value || '').trim();
        this.zipClearButton.hidden = shouldHide;
      }

      getLabel(name) {
        return this.dataset[name] || '';
      }

      getStoredZip() {
        try {
          return window.localStorage.getItem(this.dataset.pickupZipStorageKey || 'pickup-availability-zip') || '';
        } catch (error) {
          return '';
        }
      }

      storeZip(zip) {
        try {
          window.localStorage.setItem(this.dataset.pickupZipStorageKey || 'pickup-availability-zip', zip);
        } catch (error) {
          return;
        }
      }

      clearStoredZip() {
        try {
          window.localStorage.removeItem(this.dataset.pickupZipStorageKey || 'pickup-availability-zip');
        } catch (error) {
          return;
        }
      }
    }
  );
}

if (!customElements.get('pickup-availability-drawer')) {
  customElements.define(
    'pickup-availability-drawer',
    class PickupAvailabilityDrawer extends HTMLElement {
      constructor() {
        super();

        this.onBodyClick = this.handleBodyClick.bind(this);

        this.querySelector('button').addEventListener('click', () => {
          this.hide();
        });

        this.addEventListener('keyup', (event) => {
          if (event.code.toUpperCase() === 'ESCAPE') this.hide();
        });
      }

      handleBodyClick(evt) {
        const target = evt.target;
        if (
          target != this &&
          !target.closest('pickup-availability-drawer') &&
          target.id != 'ShowPickupAvailabilityDrawer'
        ) {
          this.hide();
        }
      }

      hide() {
        this.removeAttribute('open');
        document.body.removeEventListener('click', this.onBodyClick);
        document.body.classList.remove('overflow-hidden');
        removeTrapFocus(this.focusElement);
      }

      show(focusElement) {
        this.focusElement = focusElement;
        this.setAttribute('open', '');
        document.body.addEventListener('click', this.onBodyClick);
        document.body.classList.add('overflow-hidden');
        trapFocus(this);
      }
    }
  );
}
